import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, Megaphone, RefreshCw } from 'lucide-react';
import WinnerFormModal from './WinnerFormModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

export const TIER_STYLE = {
  gold: { label: 'Gold', bg: '#B8884A', fg: '#FFFFFF' },
  silver: { label: 'Silver', bg: '#8E8E8E', fg: '#FFFFFF' },
  bronze: { label: 'Bronze', bg: '#9A6B3F', fg: '#FFFFFF' },
};

export const TierBadge = ({ tier }) => {
  const s = TIER_STYLE[tier] || TIER_STYLE.gold;
  return (
    <span className="inline-block text-[9.5px] tracking-[0.28em] uppercase px-2.5 py-1 font-semibold" style={{ background: s.bg, color: s.fg }} data-testid={`tier-badge-${tier}`}>
      {s.label}
    </span>
  );
};

const AdminWinnersTab = () => {
  const [winners, setWinners] = useState([]);
  const [settings, setSettings] = useState({ announced: false });
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // null | 'new' | winner
  const [deleteId, setDeleteId] = useState(null);
  const [importConfirm, setImportConfirm] = useState(false);

  const importMaster = async () => {
    setSaving(true);
    try {
      const { data } = await ax.post(`${API}/admin/winners/import-master`);
      toast.success(`Imported ${data.imported} winners from the master sheet.`);
      setImportConfirm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed.'); }
    finally { setSaving(false); }
  };

  const load = async () => {
    try {
      const [{ data }, opts] = await Promise.all([ax.get(`${API}/admin/winners`), ax.get(`${API}/admin/winners/nomination-options`)]);
      setWinners(data.winners || []);
      setSettings(data.settings || { announced: false });
      setOptions(opts.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load winners.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (modal === 'new') await ax.post(`${API}/admin/winners`, payload);
      else await ax.patch(`${API}/admin/winners/${modal.id}`, payload);
      toast.success('Winner saved.');
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const togglePublished = async (w) => {
    try { await ax.patch(`${API}/admin/winners/${w.id}`, { published: !w.published }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed.'); }
  };

  const toggleAnnounced = async () => {
    try {
      const { data } = await ax.put(`${API}/admin/winners-settings`, { announced: !settings.announced });
      setSettings(data);
      toast.success(data.announced ? 'Winners are now LIVE on the public Winners page.' : 'Winners page reverted to the placeholder.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed.'); }
  };

  const remove = async () => {
    try { await ax.delete(`${API}/admin/winners/${deleteId}`); toast.success('Removed.'); setDeleteId(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    winners.forEach((w) => {
      const key = `${w.pillar}|||${w.category}`;
      if (!map.has(key)) map.set(key, { pillar: w.pillar, category: w.category, items: [] });
      map.get(key).items.push(w);
    });
    return [...map.values()];
  }, [winners]);

  const published = winners.filter((w) => w.published).length;

  if (loading) return <div className="py-16 text-center text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs">Loading…</div>;

  return (
    <div data-testid="admin-winners-tab">
      <div className="border border-[var(--gold-faint)] bg-white p-6 flex flex-wrap items-center justify-between gap-5 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]">Public Winners Page</div>
          <h3 className="font-serif text-lg text-[var(--ink)] mt-1" data-testid="winners-announce-status">
            {settings.announced ? 'Winners are LIVE' : 'Placeholder showing · Winners announced on 8th October'}
          </h3>
          <p className="text-[12.5px] text-[var(--ink-muted)] mt-1.5">{winners.length} winner{winners.length === 1 ? '' : 's'} saved · {published} published. Only published winners appear once announced.</p>
        </div>
        <button onClick={toggleAnnounced} data-testid="winners-announce-toggle"
          className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] tracking-[0.28em] uppercase transition-colors ${settings.announced ? 'border border-[#C2613A] text-[#C2613A] hover:bg-[#C2613A] hover:text-white' : 'bg-[var(--walnut)] text-white hover:bg-[var(--gold)]'}`}>
          <Megaphone size={13} /> {settings.announced ? 'Un-announce (hide winners)' : 'Announce Winners'}
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <p className="text-[var(--ink-muted)] text-sm">Gold / Silver / Bronze per category. Pick from submitted entries or type freely.</p>
        <div className="flex items-center gap-3">
          <button onClick={() => setImportConfirm(true)} data-testid="winner-import-master" className="inline-flex items-center gap-2 border border-[var(--gold-faint)] hover:border-[var(--gold)] text-[var(--ink-muted)] hover:text-[var(--gold)] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase transition-colors">
            <RefreshCw size={13} /> Re-import master sheet
          </button>
          <button onClick={() => setModal('new')} data-testid="winner-add" className="inline-flex items-center gap-2 bg-[var(--walnut)] text-white px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase">
            <Plus size={13} /> Add Winner
          </button>
        </div>
      </div>

      {grouped.length === 0 ? (
        <div className="border border-dashed border-[var(--gold-faint)] p-12 text-center text-[var(--ink-muted)] text-sm" data-testid="winners-empty">No winners added yet.</div>
      ) : (
        <div className="space-y-5">
          {grouped.map((g) => (
            <div key={`${g.pillar}|${g.category}`} className="border border-[var(--gold-faint)] bg-white" data-testid={`winners-group-${g.category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="px-5 py-3 border-b border-[var(--gold-faint)]/60 flex items-baseline gap-3">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)]">{g.pillar}</span>
                <span className="font-serif text-[var(--ink)]">{g.category}</span>
              </div>
              <div className="divide-y divide-[var(--gold-faint)]/50">
                {g.items.map((w) => (
                  <div key={w.id} className="px-5 py-3.5 flex flex-wrap items-center gap-4" data-testid={`winner-row-${w.id}`}>
                    <TierBadge tier={w.tier} />
                    <div className="flex-1 min-w-[220px]">
                      <div className="text-[var(--ink)] text-sm font-medium">{w.awarded_to || w.organisation}{w.entry_no ? <span className="text-[var(--ink-muted)] numeral ml-2">#{w.entry_no}</span> : null}</div>
                      <div className="text-[12px] text-[var(--ink-muted)] mt-0.5">{[w.entry_title && `“${w.entry_title}”`, w.organisation && w.organisation !== w.awarded_to && `Brand: ${w.organisation}`, w.agency && `Agency: ${w.agency}`].filter(Boolean).join(' · ')}</div>
                    </div>
                    <button onClick={() => togglePublished(w)} data-testid={`winner-publish-${w.id}`} className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border ${w.published ? 'border-[#4F8A5B] text-[#4F8A5B]' : 'border-[var(--gold-faint)] text-[var(--ink-muted)]'}`}>
                      {w.published ? <><Eye size={12} /> Published</> : <><EyeOff size={12} /> Draft</>}
                    </button>
                    <button onClick={() => setModal(w)} data-testid={`winner-edit-${w.id}`} className="text-[var(--ink-muted)] hover:text-[var(--gold)]"><Pencil size={15} /></button>
                    <button onClick={() => setDeleteId(w.id)} data-testid={`winner-delete-${w.id}`} className="text-[var(--ink-muted)] hover:text-[#C2613A]"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <WinnerFormModal initial={modal === 'new' ? null : modal} options={options} onClose={() => setModal(null)} onSave={save} saving={saving} />
      )}

      {importConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="winner-import-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setImportConfirm(false)} />
          <div className="relative bg-white border border-[var(--gold-faint)] max-w-md w-full p-7">
            <h3 className="font-serif text-lg text-[var(--ink)]">Re-import the approved master sheet?</h3>
            <p className="text-sm text-[var(--ink-muted)] mt-2">This replaces every winner currently saved with the approved list (all published). Manual edits made here will be lost.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setImportConfirm(false)} className="border border-[var(--gold-faint)] text-[var(--ink-muted)] px-4 py-2 text-[10px] tracking-[0.28em] uppercase">Cancel</button>
              <button onClick={importMaster} disabled={saving} data-testid="winner-import-confirm" className="bg-[var(--walnut)] text-white px-4 py-2 text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">{saving ? 'Importing…' : 'Re-import'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="winner-delete-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteId(null)} />
          <div className="relative bg-white border border-[var(--gold-faint)] max-w-sm w-full p-7">
            <h3 className="font-serif text-lg text-[var(--ink)]">Remove this winner?</h3>
            <p className="text-sm text-[var(--ink-muted)] mt-2">This cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="border border-[var(--gold-faint)] text-[var(--ink-muted)] px-4 py-2 text-[10px] tracking-[0.28em] uppercase">Cancel</button>
              <button onClick={remove} data-testid="winner-delete-confirm" className="bg-[#C2613A] text-white px-4 py-2 text-[10px] tracking-[0.28em] uppercase">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWinnersTab;
